import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingCart, Eye } from 'lucide-react';
import { useShop } from '../../context/ShopContext';
import { useProducts, useFeaturedProducts } from '../../hooks/useShopify';
import { Link } from 'react-router-dom';

const ProductReel = ({ title, subtitle, featured = false }) => {
  const { openQuickView, addToCart } = useShop();
  const { products: allProducts, loading: allLoading, error: allError } = useProducts();
  const { products: featuredProducts, loading: featuredLoading } = useFeaturedProducts();

  const loading = featured ? featuredLoading : allLoading;
  const error = featured ? null : allError;
  const products = featured ? featuredProducts.slice(0, 4) : allProducts.slice(0, 4);

  if (error) {
     return <div className="text-center py-10 text-destructive">Kunne ikke laste produkter.</div>;
  }

  return (
    <section className="py-16 bg-secondary/20">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-end mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-2">{title}</h2>
            {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
          </div>
          <Link to="/category/alle">
            <Button variant="link" className="text-primary font-semibold hover:text-accent hidden md:flex">
                Se alle produkter
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
          {loading ? (
             // Skeleton Loading State
             Array.from({ length: 4 }).map((_, i) => (
               <div key={i} className="flex flex-col space-y-3">
                 <Skeleton className="h-[200px] sm:h-[300px] w-full rounded-xl" />
                 <div className="space-y-2">
                   <Skeleton className="h-4 w-[250px]" />
                   <Skeleton className="h-4 w-[200px]" />
                 </div>
               </div>
             ))
          ) : (
            // Data Loaded
            products.map((product) => (
              <div 
                key={product.id} 
                className="group bg-card rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border overflow-hidden flex flex-col h-full"
              >
                {/* Image Container */}
                <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden bg-muted">
                  {product.badge && (
                    <Badge className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-accent text-white border-none px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs shadow-sm">
                      {product.badge}
                    </Badge>
                  )}
                  
                  {/* Hover Actions - desktop only */}
                  <div className="absolute top-3 right-3 z-10 hidden sm:flex opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0 pointer-events-none group-hover:pointer-events-auto">
                     <Button
                      size="icon"
                      variant="secondary"
                      className="h-8 w-8 rounded-full shadow-sm hover:bg-white hover:text-primary cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openQuickView(product);
                      }}
                      title="Hurtigvisning"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>

                  <Link to={`/product/${product.handle || product.id}`} className="block w-full h-full cursor-pointer">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=400&auto=format&fit=crop'; }}
                    />
                  </Link>
                  
                  {/* Quick Add Button - desktop hover only */}
                  <div className="absolute bottom-4 left-4 right-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto hidden sm:block">
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 text-white shadow-lg cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addToCart(product);
                      }}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" /> Legg i handlekurv
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-5 flex flex-col flex-grow">
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1 font-medium tracking-wide">{product.category}</p>
                  <Link to={`/product/${product.handle || product.id}`} className="block mb-0.5 sm:mb-1 cursor-pointer">
                    <h3 className="font-semibold text-sm sm:text-lg leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">{product.name}</h3>
                  </Link>
                  <p className="text-[10px] sm:text-xs italic text-muted-foreground/80 mb-2 sm:mb-3 font-serif hidden sm:block">{product.scientific}</p>
                  
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex flex-col">
                       {product.originalPrice && (
                        <span className="text-[10px] sm:text-xs text-muted-foreground line-through">kr {product.originalPrice},-</span>
                      )}
                      <span className="text-sm sm:text-xl font-bold text-primary">kr {product.price},-</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground items-center gap-1 bg-secondary/30 px-2 py-1 rounded-full hidden sm:flex">
                       <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> På lager
                    </div>
                  </div>

                  {/* Touch-friendly add to cart - mobile only */}
                  <Button 
                    size="sm"
                    className="mt-2 w-full bg-primary hover:bg-primary/90 text-white text-xs h-8 rounded-full sm:hidden"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addToCart(product);
                    }}
                  >
                    <ShoppingCart className="mr-1 h-3 w-3" /> Legg i kurv
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="mt-8 text-center md:hidden">
            <Link to="/category/alle">
                <Button variant="outline" className="w-full">Se alle produkter</Button>
            </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductReel;
